import React, { useCallback } from 'react';
import { css, styled } from 'styled-components';
import { COLORS, STYLE, computeRemSize } from '@ydistri/theme';
import { Text } from '../typography/Typography/Typography';
import { MdCopyAll } from 'react-icons/md';
import { IconButton } from '../IconButton/IconButton';

export type CopyableDataSectionVariant = 'neutral' | 'success' | 'danger';

export interface CopyableDataSectionProps {
  title: React.ReactNode;
  data: (number | string)[];
  dataToDisplay?: (number | string)[];
  emptyText?: string;
  variant?: CopyableDataSectionVariant;
  maxHeight?: string;
  className?: string;
  onCopySuccess?: (text: string) => void;
  onCopyError?: (text: string, error: Error) => void;
}

const variantStyles = {
  neutral: css`
    background-color: ${COLORS.WHITE};
    border-color: ${COLORS.BORDER};
  `,
  success: css`
    background-color: ${COLORS.TABLE_HIGHLIGHT_GREEN};
    border-color: ${COLORS.SUCCESS};
  `,
  danger: css`
    background-color: ${COLORS.TABLE_HIGHLIGHT_RED};
    border-color: ${COLORS.DANGER};
  `,
};

const CopyableDataSectionWrapper = styled.section<{
  $variant: CopyableDataSectionVariant;
}>`
  display: flex;
  flex-direction: column;
  border: 1px solid ${COLORS.BORDER};
  border-radius: ${computeRemSize(5)};
  overflow: hidden;

  ${({ $variant }) => variantStyles[$variant]}
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem;
  border-bottom: 1px solid ${COLORS.BORDER};
  background-color: rgba(255, 255, 255, 0.72);
`;

const SectionContent = styled.pre<{ $maxHeight: string; $empty: boolean }>`
  margin: 0;
  padding: 0.75rem;
  max-height: ${({ $maxHeight }) => $maxHeight};
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ $empty }) => ($empty ? COLORS.GREY : COLORS.COLOR_DARK)};
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  font-size: ${STYLE.TEXT_FONT_SIZE.small};

  ${STYLE.SCROLLBAR}
`;

const copyIcon = <MdCopyAll />;

/**
 * Displays a titled section of line-separated values with a single action to copy them all.
 * Useful for showing identifiers, codes, or other raw data that should stay easy to scan,
 * preserve line breaks, and be copied to the clipboard as plain text.
 * `data` is the source used for copying, while `dataToDisplay` can be used to render a
 * different, more user-friendly representation in the UI.
 * @param title
 * @param data
 * @param dataToDisplay
 * @param emptyText text rendered when there is no data to show
 * @param variant visual style of the section
 * @param maxHeight maximum height of the scrollable content area
 * @param className optional wrapper class name
 * @param onCopySuccess callback invoked after text is successfully copied
 * @param onCopyError callback invoked when copying fails
 */
export const CopyableDataSection: React.FC<CopyableDataSectionProps> = ({
  title,
  data,
  dataToDisplay,
  emptyText = 'No data',
  variant = 'neutral',
  maxHeight = '14rem',
  className,
  onCopySuccess,
  onCopyError,
}) => {
  const copyText = data.map(item => item.toString()).join('\n');
  const valuesToDisplay = dataToDisplay ?? data;
  const displayText =
    valuesToDisplay.length === 0
      ? emptyText
      : valuesToDisplay.map(item => item.toString()).join('\n');
  const hasData = data.length > 0;

  const copyToClipboard = useCallback(() => {
    if (!hasData) return;

    navigator.clipboard
      .writeText(copyText)
      .then(() => {
        onCopySuccess?.(copyText);
      })
      .catch((error: unknown) => {
        const err = error instanceof Error ? error : new Error(String(error));
        onCopyError?.(copyText, err);
      });
  }, [copyText, hasData, onCopyError, onCopySuccess]);

  return (
    <CopyableDataSectionWrapper
      className={className}
      $variant={variant}
      data-type="CopyableDataSection"
      data-variant={variant}
    >
      <SectionTitleRow>
        <Text $semiBold>{title}</Text>
        <IconButton
          size="xSmall"
          onClick={copyToClipboard}
          disabled={!hasData}
          icon={copyIcon}
          tooltip="Copy all identifiers"
        />
      </SectionTitleRow>
      <SectionContent
        $maxHeight={maxHeight}
        $empty={!hasData}
        data-type="CopyableDataSectionContent"
      >
        {displayText}
      </SectionContent>
    </CopyableDataSectionWrapper>
  );
};
